import type { InfoClient } from "@nktkas/hyperliquid";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { queryKeys } from "#/hooks/query-keys";
import { getInfoClient, stripDexPrefix } from "../lib/hyperliquid";
import { useHyperliquidSubscription } from "./use-hyperliquid-subscription";
import { useTradeMarket } from "./use-trade-market";

type RecentTrade = Awaited<ReturnType<InfoClient["recentTrades"]>>[number];

function createFallbackTrades(coin: string, price: number): RecentTrade[] {
	return Array.from({ length: 18 }, (_, index) => {
		const side = index % 2 === 0 ? "B" : "A";
		const delta = (index % 5) * (price > 100 ? 1.2 : 0.01);
		return {
			coin,
			side,
			px: (side === "B" ? price - delta : price + delta).toFixed(
				price > 100 ? 1 : 4,
			),
			sz: (0.02 + index * 0.006).toFixed(4),
			time: Date.now() - index * 6_000,
			hash: `0x${`${index}`.padStart(64, "0")}`,
			tid: 10_000 + index,
			users: [
				"0x1111111111111111111111111111111111111111",
				"0x2222222222222222222222222222222222222222",
			],
		};
	});
}

export function useTradeRecentTrades() {
	const { market, marketData } = useTradeMarket();
	const infoClient = getInfoClient();
	const apiCoin = market.chartSymbol;
	const [liveTradeState, setLiveTradeState] = useState<{
		coin: string;
		trades: RecentTrade[];
	}>({ coin: apiCoin, trades: [] });

	const tradesQuery = useQuery({
		queryKey: queryKeys.trade.recentTrades(apiCoin),
		queryFn: () => infoClient.recentTrades({ coin: apiCoin }),
		staleTime: 300_000,
	});

	useHyperliquidSubscription({
		deps: [apiCoin],
		onBeforeSubscribe: () => {
			setLiveTradeState({ coin: apiCoin, trades: [] });
		},
		subscribe: (client) =>
			client.trades({ coin: apiCoin }, (payload) => {
				setLiveTradeState((current) => {
					const currentTrades = current.coin === apiCoin ? current.trades : [];
					const seen = new Set(currentTrades.map((trade) => trade.tid));
					const next = [
						...payload.filter((trade) => !seen.has(trade.tid)),
						...currentTrades,
					];
					return { coin: apiCoin, trades: next.slice(0, 24) };
				});
			}),
	});

	const liveTrades =
		liveTradeState.coin === apiCoin ? liveTradeState.trades : [];
	const queryTrades = (tradesQuery.data ?? []).filter(
		(trade) =>
			trade.coin === apiCoin || stripDexPrefix(trade.coin) === market.coin,
	);
	const seen = new Set<number>();
	const merged = [...liveTrades, ...queryTrades];
	const trades = merged.filter((trade) => {
		if (seen.has(trade.tid)) return false;
		seen.add(trade.tid);
		return true;
	});

	return {
		...tradesQuery,
		trades:
			trades.length > 0
				? trades
				: createFallbackTrades(market.coin, marketData.markPrice || 0),
	};
}
